import React, { useState } from 'react';
import { Alert, Button, Form, InputGroup, Modal } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import { Api } from 'src/models/Api.model';
import { I18NextNS } from 'src/models/Language.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useValidatePassword from 'src/hooks/useValidatePassword';

import CustomToast from './CustomToast';

export type ChangePwdModalProps = {
  isShowChangePwdModal: boolean;
  handleCloseChangePwdModal: () => void;
  canBeCanceled?: boolean;
};

export default function ChangePwdModal({
  isShowChangePwdModal,
  handleCloseChangePwdModal,
  canBeCanceled,
}: ChangePwdModalProps) {
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    defaultValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
    criteriaMode: 'all',
  });

  const { t } = useTranslation(I18NextNS.PAGES);

  const { handleHttpRequestError } = useHandleHttpRequestError();

  const [isShowToast, setIsShowToast] = useState(false);
  const [toastText, setToastText] = useState('');

  const handleShowToast = (text: string) => {
    setToastText(text);
    setIsShowToast(true);
  };

  const { currentUser } = useRemoteBoundStore();

  const userId = currentUser?.userId;

  const closeModalAndReset = () => {
    handleCloseChangePwdModal();
    reset();
  };

  const onSubmit: SubmitHandler<{
    currentPassword: string;
    password: string;
    confirmPassword: string;
  }> = async (data) => {
    try {
      const { currentPassword, password } = data;

      if (!userId) {
        throw new Error(
          t('message.error.userIdNotFound', { ns: I18NextNS.COMMON }),
        );
      }

      await Api.remote.change_password(userId, currentPassword, password);

      closeModalAndReset();
      handleShowToast(t('changePassword.passwordChanged'));
    } catch (error) {
      const errorMessage = handleHttpRequestError(error);
      handleShowToast(errorMessage);
    }
  };

  const [isShowCurrentPassword, setIsShowCurrentPassword] = useState(false);
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);

  const validatePassword = useValidatePassword();

  return (
    <>
      <CustomModal
        show={isShowChangePwdModal}
        keyboard={canBeCanceled}
        onHide={closeModalAndReset}
      >
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Header closeButton={canBeCanceled}>
            <Modal.Title>{t('changePassword.title')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!canBeCanceled && (
              <Alert variant="danger">
                {t('changePassword.passwordTooWeak')}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>{t('changePassword.currentPassword')}</Form.Label>
              <InputGroup>
                <Form.Control
                  type={isShowCurrentPassword ? 'text' : 'password'}
                  placeholder={t('changePassword.currentPasswordPlaceholder')}
                  {...register('currentPassword', {
                    required: t('changePassword.currentPasswordRequired'),
                  })}
                />
                <InputGroup.Text
                  onClick={() =>
                    setIsShowCurrentPassword(!isShowCurrentPassword)
                  }
                  className="pointer"
                >
                  <FontAwesomeIcon
                    icon={isShowCurrentPassword ? faEye : faEyeSlash}
                  />
                </InputGroup.Text>
              </InputGroup>
              {errors.currentPassword?.message && (
                <p className="text-danger">{errors.currentPassword?.message}</p>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('changePassword.newPassword')}</Form.Label>
              <InputGroup>
                <Form.Control
                  type={isShowPassword ? 'text' : 'password'}
                  placeholder={t('changePassword.newPasswordPlaceholder')}
                  {...register('password', {
                    required: t('changePassword.passwordRequired'),
                    validate: validatePassword,
                  })}
                />
                <InputGroup.Text
                  onClick={() => setIsShowPassword(!isShowPassword)}
                  className="pointer"
                >
                  <FontAwesomeIcon icon={isShowPassword ? faEye : faEyeSlash} />
                </InputGroup.Text>
              </InputGroup>
              <div className="text-danger">
                {errors.password?.type === 'required' ? (
                  <p>{t('changePassword.newPasswordRequired')}</p>
                ) : (
                  <ul className="list-unstyled">
                    {errors.password?.types?.minLength && (
                      <li>{errors.password.message}</li>
                    )}
                    {errors.password?.types?.hasUpperCase && (
                      <li>{errors.password.types.hasUpperCase}</li>
                    )}
                    {errors.password?.types?.hasLowerCase && (
                      <li>{errors.password.types.hasLowerCase}</li>
                    )}
                    {errors.password?.types?.hasNumber && (
                      <li>{errors.password.types.hasNumber}</li>
                    )}
                    {errors.password?.types?.hasSpecialChar && (
                      <li>{errors.password.types.hasSpecialChar}</li>
                    )}
                  </ul>
                )}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('changePassword.confirmPassword')}</Form.Label>
              <InputGroup>
                <Form.Control
                  type={isShowConfirmPassword ? 'text' : 'password'}
                  placeholder={t('changePassword.confirmPasswordPlaceholder')}
                  {...register('confirmPassword', {
                    required: t('changePassword.confirmPasswordRequired'),
                    validate: (val: string) => {
                      if (watch('password') !== val) {
                        return t('changePassword.passwordsDoNotMatch');
                      }
                    },
                  })}
                />
                <InputGroup.Text
                  onClick={() =>
                    setIsShowConfirmPassword(!isShowConfirmPassword)
                  }
                  className="pointer"
                >
                  <FontAwesomeIcon
                    icon={isShowConfirmPassword ? faEye : faEyeSlash}
                  />
                </InputGroup.Text>
              </InputGroup>
              {
                <p className="text-danger ">
                  {errors.confirmPassword?.message}
                </p>
              }
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            {canBeCanceled && (
              <Button variant="outline-primary" onClick={closeModalAndReset}>
                {t('changePassword.cancel')}
              </Button>
            )}
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {t('changePassword.change')}
            </Button>
          </Modal.Footer>
        </Form>
      </CustomModal>

      <CustomToast
        children={toastText}
        isShow={isShowToast}
        handleToggleShowToast={(isShow) => setIsShowToast(isShow)}
      />
    </>
  );
}
